import { useEffect, useState } from 'react'
import { useParams, useLocation, NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  FiArrowLeft, FiUsers, FiClock, FiBarChart2, FiShield, FiBriefcase, FiLogOut,
  FiMapPin, FiCalendar, FiLayers, FiLink
} from 'react-icons/fi'
import { EmpresaGestionProvider } from '../../contexts/EmpresaGestionContext'
import { empresasAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Btn, Pill } from '../../ui'
import toast from 'react-hot-toast'

/*
 * Sub-shell para que un super admin gestione una empresa concreta.
 * Se renderiza fuera de AppShellV2 (no tiene sidebar global), por eso
 * conserva su propio header con botón de volver y logout.
 */

const planLabel = (p) => ({ pro: 'Pro', avanzado: 'Avanzado', trial: 'Trial' }[p] || p)

const GestionEmpresaAdminV2 = () => {
  const { empresaId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [empresa, setEmpresa] = useState(location.state?.empresa || null)
  const [loading, setLoading] = useState(!location.state?.empresa)

  useEffect(() => {
    if (!empresa && empresaId) cargarEmpresa()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId])

  useEffect(() => {
    if (!empresa) return
    const esIndex = location.pathname === `/admin/empresa/${empresaId}` ||
                    location.pathname === `/admin/empresa/${empresaId}/`
    if (!esIndex) return
    navigate(`/admin/empresa/${empresaId}/${empresa.tipo === 'asesoria' ? 'clientes' : 'empleados'}`, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa])

  const cargarEmpresa = async () => {
    try {
      setLoading(true)
      const r = await empresasAPI.getById(empresaId)
      setEmpresa(r.data.empresa)
    } catch (e) {
      console.error(e)
      toast.error('Error al cargar empresa')
      navigate('/v2/empresas')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--ink-3)] border-t-transparent" />
      </div>
    )
  }
  if (!empresa) return null

  const empresaBloqueada = empresa.fecha_expiracion && new Date(empresa.fecha_expiracion) <= new Date()
  const esAsesoria = empresa.tipo === 'asesoria'

  const tabs = [
    { name: esAsesoria ? 'Gestores' : 'Empleados', href: `/admin/empresa/${empresaId}/empleados`, icon: FiUsers },
    ...(esAsesoria ? [{ name: 'Clientes', href: `/admin/empresa/${empresaId}/clientes`, icon: FiLink }] : []),
    ...(!esAsesoria ? [
      { name: 'Grupos',           href: `/admin/empresa/${empresaId}/grupos`,           icon: FiLayers },
      { name: 'Calendarios',      href: `/admin/empresa/${empresaId}/calendarios`,      icon: FiCalendar },
      { name: 'Centros',          href: `/admin/empresa/${empresaId}/centros-trabajo`,  icon: FiMapPin },
      { name: 'Tipos pausa',      href: `/admin/empresa/${empresaId}/tipos-pausa`,      icon: FiClock },
      { name: 'Tipos ausencia',   href: `/admin/empresa/${empresaId}/tipos-ausencia`,   icon: FiCalendar },
      { name: 'Gestión fichajes', href: `/admin/empresa/${empresaId}/gestion`,          icon: FiClock },
      { name: 'Reportes',         href: `/admin/empresa/${empresaId}/reportes`,         icon: FiBarChart2 },
      { name: 'Auditoría',        href: `/admin/empresa/${empresaId}/auditoria`,        icon: FiShield },
      { name: 'Datos empresa',    href: `/admin/empresa/${empresaId}/configuracion`,    icon: FiBriefcase },
    ] : []),
  ]

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header */}
      <div className="border-b border-[var(--line)] bg-[var(--bg)]">
        <div className="max-w-7xl mx-auto px-7 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <Btn variant="ghost" size="sm" onClick={() => navigate('/v2/empresas')}>
              <FiArrowLeft size={14} /> Empresas
            </Btn>
            <div className="border-l border-[var(--line)] pl-4 min-w-0">
              <div className="eyebrow flex items-center gap-2">
                <span className="font-mono">§ Modo super admin</span>
                {empresaBloqueada && <Pill tone="red">Bloqueada · solo lectura</Pill>}
              </div>
              <h1 className="font-display text-[26px] leading-[1.05] text-[var(--ink)] truncate mt-1">
                {empresa.nombre}
                {esAsesoria && <span className="ml-2 text-[14px] text-[var(--ink-3)] font-sans">· Asesoría</span>}
                {empresa.tipo === 'cliente_interno' && <span className="ml-2 text-[14px] text-[var(--ink-3)] font-sans">· Cliente interno</span>}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-[12px] leading-tight">
              <div>
                <span className="text-[var(--ink-3)]">Plan </span>
                <span className="text-[var(--ink)] font-medium">{planLabel(empresa.plan)}</span>
                {empresa.subscription_status && (
                  <span className="text-[var(--ink-3)] capitalize"> · {empresa.subscription_status}</span>
                )}
              </div>
              <div className="mt-0.5">
                <span className="text-[var(--ink-3)]">Empleados </span>
                <span className="text-[var(--ink)] font-medium tnum">
                  {empresa.empleados_activos || 0}
                  {empresa.slots_total > 0 ? `/${empresa.slots_total} slots` : ''}
                </span>
              </div>
            </div>
            <Btn variant="ghost" size="sm" onClick={logout} title="Cerrar sesión">
              <FiLogOut size={14} /> Salir
            </Btn>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-7">
          <nav className="flex gap-1 overflow-x-auto -mb-px">
            {tabs.map((tab) => (
              <NavLink
                key={tab.name}
                to={tab.href}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3 py-3 text-[13px] border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-[var(--ink)] text-[var(--ink)] font-medium'
                      : 'border-transparent text-[var(--ink-3)] hover:text-[var(--ink)] hover:border-[var(--line-2)]'
                  }`
                }
              >
                <tab.icon size={14} />
                {tab.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Outlet */}
      <div className="max-w-7xl mx-auto px-7 py-6">
        <EmpresaGestionProvider empresaId={empresaId} empresa={empresa}>
          <Outlet context={{ empresaId, empresa }} />
        </EmpresaGestionProvider>
      </div>
    </div>
  )
}

export default GestionEmpresaAdminV2
